import type {
  WalletPnlPointDto,
  WalletPositionDto,
  WalletProfileDto,
} from '@root/shared/api/ServicesApi'

/**
 * How one wallet has traded, across every token it has touched.
 *
 * The backend answers in strings because the figures are decimals wider than
 * a double, and this turns the ones a screen does arithmetic or comparisons
 * on into numbers once, here, rather than at each place that draws them.
 *
 * `costBasisKnown` is the one field worth pausing over. A swap in a pool
 * priced in neither SOL nor a stablecoin reaches us with no dollar figure at
 * all, so for those positions the PnL is not zero — it is unknown, and the UI
 * has to say so rather than draw a flat line through them.
 */
export type Position = {
  token: string
  /** Empty until the indexer has read the mint's metadata. */
  name: string
  symbol: string
  /** A miss is a 404 and the caller's own placeholder. */
  logo: string
  pairAddress: string
  boughtTokens: number
  soldTokens: number
  remainingTokens: number
  spentUsd: number
  receivedUsd: number
  buys: number
  sells: number
  realizedPnl: number
  unrealizedPnl: number
  pnl: number
  priceUsd: number
  /** Whether anything recorded what the buys cost; see above. */
  costBasisKnown: boolean
  /** The market cap the wallet bought in at, where it is known. */
  avgBuyMarketCap: number | null
  avgBuyPrice: number | null
  avgSellPrice: number | null
  firstTradeAt: number
  lastTradeAt: number
}

export type PnlPoint = {
  /** The day, as `YYYY-MM-DD`. */
  day: string
  /** Cumulative, so the last day is the wallet's realized PnL. */
  realizedPnl: number
  buyVolumeUsd: number
  sellVolumeUsd: number
  buys: number
  sells: number
}

export type Totals = {
  realizedPnl: number
  unrealizedPnl: number
  totalPnl: number
  spentUsd: number
  receivedUsd: number
  tokensTraded: number
  unpricedPositions: number
  txes: number
  buys: number
  sells: number
  sharpe: number | null
  /** Positions the wallet has fully sold out of. */
  closedPositions: number
  /** Of those, how many came out ahead. */
  winningClosedPositions: number
  /** Mean seconds between a position's first and last trade. */
  avgHoldSeconds: number | null
}

/** How the wallet entered, by the market cap it bought into. */
export type EntryMarketCaps = {
  below100k: number
  from100kTo500k: number
  above500k: number
  unknown: number
}

export type Distribution = {
  gainOver500: number
  gain200To500: number
  gain0To200: number
  loss0To50: number
  lossOver50: number
}

/** Where the wallet's money first came from, when that can be said. */
export type Funding = {
  sender: string
  mint: string
  amount: number
  /** Unix milliseconds. */
  at: number
  /**
   * Whether this is plausibly the first money in, rather than a later top-up.
   *
   * False means the wallet was already trading before the earliest transfer
   * we hold, so it was funded before anyone was watching. Showing it as the
   * funding would date an old wallet as a fresh one.
   */
  looksOriginal: boolean
}

/**
 * Whose wallet this is, when it belongs to somebody here.
 *
 * Absent for a stranger's, and for anybody who has gone incognito — which is
 * the same absence, deliberately: the screen cannot tell the two apart and
 * does not need to.
 */
export type Owner = {
  delugeId: string
  username: string
  avatar?: string
  followers: number
  following: number
  posts: number
}

export type WalletProfile = {
  walletAddress: string
  owner?: Owner
  totals: Totals
  distribution: Distribution
  entryMarketCaps: EntryMarketCaps
  funding: Funding | undefined
  realizedPnlSeries: Array<PnlPoint>
  positions: Array<Position>
}

/**
 * An account with no wallets, which has traded nothing.
 *
 * Not a placeholder: zero is the true answer for somebody who has no wallet
 * for us to look at. The alternative — holding the screen on a skeleton until
 * a wallet appears — is a page that never resolves and never says why.
 */
export const EMPTY: WalletProfile = {
  walletAddress: '',
  totals: {
    realizedPnl: 0,
    unrealizedPnl: 0,
    totalPnl: 0,
    spentUsd: 0,
    receivedUsd: 0,
    tokensTraded: 0,
    unpricedPositions: 0,
    txes: 0,
    buys: 0,
    sells: 0,
    // Null, not undefined: the type says "we could not compute it", which is
    // what no trades means, and the blocks below read it that way.
    // eslint-disable-next-line unicorn/no-null
    sharpe: null,
    closedPositions: 0,
    winningClosedPositions: 0,
    // eslint-disable-next-line unicorn/no-null
    avgHoldSeconds: null,
  },
  distribution: {
    gainOver500: 0,
    gain200To500: 0,
    gain0To200: 0,
    loss0To50: 0,
    lossOver50: 0,
  },
  entryMarketCaps: {
    below100k: 0,
    from100kTo500k: 0,
    above500k: 0,
    unknown: 0,
  },
  funding: undefined,
  realizedPnlSeries: [],
  positions: [],
}

const positionFromDto = (dto: WalletPositionDto): Position => ({
  token: dto.token,
  name: dto.name,
  symbol: dto.symbol,
  logo: dto.logo,
  pairAddress: dto.pairAddress,
  boughtTokens: Number(dto.boughtTokens),
  soldTokens: Number(dto.soldTokens),
  remainingTokens: Number(dto.remainingTokens),
  spentUsd: Number(dto.spentUsd),
  receivedUsd: Number(dto.receivedUsd),
  buys: dto.buys,
  sells: dto.sells,
  realizedPnl: dto.realizedPnl,
  unrealizedPnl: dto.unrealizedPnl,
  pnl: dto.pnl,
  priceUsd: Number(dto.priceUsd),
  costBasisKnown: dto.costBasisKnown,
  avgBuyMarketCap: dto.avgBuyMarketCap,
  avgBuyPrice: dto.avgBuyPrice,
  avgSellPrice: dto.avgSellPrice,
  firstTradeAt: dto.firstTradeAt,
  lastTradeAt: dto.lastTradeAt,
})

const pointFromDto = (dto: WalletPnlPointDto): PnlPoint => ({
  day: dto.day,
  realizedPnl: Number(dto.realizedPnl),
  buyVolumeUsd: Number(dto.buyVolumeUsd),
  sellVolumeUsd: Number(dto.sellVolumeUsd),
  buys: dto.buys,
  sells: dto.sells,
})

export const fromDto = (dto: WalletProfileDto): WalletProfile => ({
  walletAddress: dto.walletAddress,
  ...(dto.owner ? { owner: dto.owner } : {}),
  totals: {
    realizedPnl: Number(dto.totals.realizedPnl),
    unrealizedPnl: Number(dto.totals.unrealizedPnl),
    totalPnl: Number(dto.totals.totalPnl),
    spentUsd: Number(dto.totals.spentUsd),
    receivedUsd: Number(dto.totals.receivedUsd),
    tokensTraded: dto.totals.tokensTraded,
    unpricedPositions: dto.totals.unpricedPositions,
    txes: dto.totals.txes,
    buys: dto.totals.buys,
    sells: dto.totals.sells,
    sharpe: dto.totals.sharpe,
    closedPositions: dto.totals.closedPositions,
    winningClosedPositions: dto.totals.winningClosedPositions,
    avgHoldSeconds: dto.totals.avgHoldSeconds,
  },
  distribution: dto.distribution,
  entryMarketCaps: dto.entryMarketCaps,
  funding: dto.funding
    ? { ...dto.funding, amount: Number(dto.funding.amount) }
    : undefined,
  realizedPnlSeries: dto.realizedPnlSeries.map(pointFromDto),
  positions: dto.positions.map(positionFromDto),
})

/**
 * How often a closed position made money.
 *
 * Counted over the buckets rather than the positions, because those are what
 * the backend already sorted by outcome, and it leaves out the ones that cost
 * nothing — an airdrop sold at any price is a win by arithmetic and tells you
 * nothing about the trader.
 */
export const winRate = ({
  gainOver500,
  gain200To500,
  gain0To200,
  loss0To50,
  lossOver50,
}: Distribution): number | undefined => {
  const wins = gainOver500 + gain200To500 + gain0To200
  const total = wins + loss0To50 + lossOver50
  return total === 0 ? undefined : wins / total
}
